/**
 * @description Admin portal page for managing platform-level payment gateway configurations.
 * Reads field definitions from gatewayRegistry.js (factory pattern) so gateways are
 * interchangeable — adding a new gateway type requires only a registry entry, not UI changes.
 *
 * Key features:
 * - List all platform gateways (super_admin_gateways table)
 * - Create/edit form dynamically rendered from gateway registry field definitions
 * - Credential masking (last 4 chars shown after save)
 * - Inline validation on blur (mirrors backend ElavonConfig.validateCredentials)
 * - Config fields with type-aware rendering (text, number, select, toggle)
 * - Webhook config section with endpoint URL display
 * - Enable/disable toggle per gateway
 *
 * @dependencies
 * - adminApi: getPlatformGateways, getPlatformGateway, createPlatformGateway, updatePlatformGateway
 * - gatewayRegistry: getGatewayTypes, getGatewayConfig, validateGatewayCredentials, getDefaultConfig
 *
 * @notes
 * - Credentials stored encrypted in super_admin_gateways.credentials (TEXT)
 * - Webhook + behavior config stored in super_admin_gateways.config (JSONB)
 * - Field names match backend schemas exactly (ElavonConfig.CREDENTIAL_SCHEMA, ElavonGateway constructor)
 */

import { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Shield, CheckCircle, XCircle, AlertCircle, Loader2,
  Eye, EyeOff, Plus, ArrowLeft, ChevronRight
} from 'lucide-react';
import {
  getPlatformGateways, getPlatformGateway,
  createPlatformGateway, updatePlatformGateway
} from '../../services/api/adminApi';
import {
  getGatewayTypes, getGatewayConfig, validateGatewayCredentials, getDefaultConfig
} from '../../config/gatewayRegistry';

/**
 * CredentialField — Renders a single credential input with masking, visibility toggle, and validation
 */
function CredentialField({ field, value, maskedValue, isEditing, saving, visibleFields, onToggleVisibility, onChange, validationError }) {
  return (
    <div>
      <label htmlFor={`cred-${field.key}`} className="block text-sm font-medium text-gray-900 mb-1">
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <p className="text-xs text-gray-500 mb-2">{field.description}</p>

      {isEditing && maskedValue && !value && (
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-500 font-mono">{maskedValue}</span>
          <span className="text-xs text-gray-400">(encrypted)</span>
        </div>
      )}

      <div className="relative max-w-md">
        <input
          id={`cred-${field.key}`}
          type={field.sensitive && !visibleFields[field.key] ? 'password' : 'text'}
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          placeholder={isEditing ? `Enter new ${field.label.toLowerCase()} to replace` : field.placeholder}
          className={`w-full px-3 py-2 pr-10 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-slate-500 focus:border-slate-500 ${
            validationError ? 'border-red-300' : 'border-gray-300'
          }`}
          disabled={saving}
        />
        {field.sensitive && (
          <button
            type="button"
            onClick={() => onToggleVisibility(field.key)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            aria-label={visibleFields[field.key] ? `Hide ${field.label}` : `Show ${field.label}`}
          >
            {visibleFields[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {validationError && (
        <p className="text-xs text-red-600 mt-1">{validationError}</p>
      )}
    </div>
  );
}

/**
 * ConfigField — Renders a gateway config field based on its type (text, number, select, toggle)
 */
function ConfigField({ field, value, saving, onChange }) {
  if (field.type === 'toggle') {
    return (
      <div>
        <label htmlFor={`cfg-${field.key}`} className="flex items-center gap-3 cursor-pointer">
          <input
            id={`cfg-${field.key}`}
            type="checkbox"
            checked={value ?? field.defaultValue ?? false}
            onChange={(e) => onChange(field.key, e.target.checked)}
            className="rounded border-gray-300 text-slate-800 focus:ring-slate-500 w-5 h-5"
            disabled={saving}
          />
          <div>
            <span className="text-sm font-medium text-gray-900">{field.label}</span>
            <p className="text-xs text-gray-500">{field.description}</p>
          </div>
        </label>
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div>
        <label htmlFor={`cfg-${field.key}`} className="block text-sm font-medium text-gray-900 mb-1">
          {field.label}
        </label>
        <p className="text-xs text-gray-500 mb-2">{field.description}</p>
        <select
          id={`cfg-${field.key}`}
          value={value ?? field.defaultValue ?? ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
          disabled={saving}
        >
          {field.options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  }

  // text or number
  return (
    <div>
      <label htmlFor={`cfg-${field.key}`} className="block text-sm font-medium text-gray-900 mb-1">
        {field.label}
      </label>
      <p className="text-xs text-gray-500 mb-2">{field.description}</p>
      <input
        id={`cfg-${field.key}`}
        type={field.type === 'number' ? 'number' : 'text'}
        value={value ?? (field.defaultValue !== undefined ? field.defaultValue : '')}
        onChange={(e) => onChange(field.key, field.type === 'number' ? parseInt(e.target.value, 10) || '' : e.target.value)}
        placeholder={field.placeholder}
        className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
        disabled={saving}
      />
    </div>
  );
}

/**
 * GatewayList — Shows all platform gateways with status and actions
 */
function GatewayList({ gateways, loading, onSelect, onCreate }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          <p className="text-sm text-gray-500">Loading gateway configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <CreditCard className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Payment Gateways</h1>
            <p className="text-sm text-gray-500">Platform-level gateway configurations and credentials</p>
          </div>
        </div>
        <button
          onClick={onCreate}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Gateway
        </button>
      </div>

      {gateways.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No gateways configured</h3>
          <p className="text-sm text-gray-500 mb-4">
            Add a payment gateway to start processing payments.
          </p>
          <button
            onClick={onCreate}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Gateway
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {gateways.map(gw => (
            <button
              key={gw.id}
              onClick={() => onSelect(gw.id)}
              className="w-full bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-left hover:border-slate-300 hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <CreditCard className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">{gw.gatewayName}</h3>
                      <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded">
                        {gw.gatewayType}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Last updated {new Date(gw.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {gw.isEnabled ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                      <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                      <span className="text-xs font-medium text-green-700">Enabled</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full">
                      <XCircle className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs font-medium text-gray-500">Disabled</span>
                    </span>
                  )}
                  {gw.isDefault && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
                      Default
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * GatewayForm — Create or edit a platform gateway. Reads fields from gatewayRegistry.
 */
function GatewayForm({ gatewayId, onBack, onSaved }) {
  const [loading, setLoading] = useState(!!gatewayId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [gatewayName, setGatewayName] = useState('');
  const [gatewayType, setGatewayType] = useState('elavon');
  const [environment, setEnvironment] = useState('sandbox');
  const [isEnabled, setIsEnabled] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [notes, setNotes] = useState('');

  // Dynamic field values
  const [credentials, setCredentials] = useState({});
  const [maskedCredentials, setMaskedCredentials] = useState({});
  const [webhookConfig, setWebhookConfig] = useState({});
  const [maskedWebhook, setMaskedWebhook] = useState({});
  const [gatewayConfigValues, setGatewayConfigValues] = useState({});
  const [visibleFields, setVisibleFields] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const isEditing = !!gatewayId;
  const gwConfig = getGatewayConfig(gatewayType);

  const loadGateway = useCallback(async () => {
    if (!gatewayId) return;

    try {
      setLoading(true);
      setError('');
      const result = await getPlatformGateway(gatewayId);
      const gw = result.data || {};

      setGatewayName(gw.gatewayName || '');
      setGatewayType(gw.gatewayType || 'elavon');
      setIsEnabled(gw.isEnabled ?? true);
      setIsDefault(gw.isDefault ?? false);
      setNotes(gw.notes || '');

      // Masked credentials from API
      setMaskedCredentials(gw.credentials || {});

      // Config JSONB — contains environment, webhook fields, and behavior config
      const cfg = gw.config || {};
      setEnvironment(cfg.environment || 'sandbox');

      // Build masked webhook values from config
      const gwDef = getGatewayConfig(gw.gatewayType || 'elavon');
      if (gwDef) {
        const maskedWh = {};
        gwDef.webhookFields.forEach(field => {
          const val = cfg[field.key];
          if (val && field.sensitive) {
            maskedWh[field.key] = '****' + String(val).slice(-4);
          } else if (val) {
            maskedWh[field.key] = val;
          }
        });
        setMaskedWebhook(maskedWh);

        // Load behavior config values
        const cfgValues = {};
        gwDef.configFields.forEach(field => {
          if (cfg[field.key] !== undefined) {
            cfgValues[field.key] = cfg[field.key];
          }
        });
        setGatewayConfigValues(cfgValues);
      }

      // Reset input fields
      setCredentials({});
      setWebhookConfig({});
    } catch (err) {
      console.error('Error loading gateway:', err);
      setError('Failed to load gateway configuration');
    } finally {
      setLoading(false);
    }
  }, [gatewayId]);

  useEffect(() => {
    loadGateway();
  }, [loadGateway]);

  // Initialize default config values for new gateways
  useEffect(() => {
    if (!isEditing && gwConfig) {
      setGatewayConfigValues(getDefaultConfig(gatewayType));
    }
  }, [gatewayType, isEditing, gwConfig]);

  const handleCredentialChange = (key, value) => {
    setCredentials(prev => ({ ...prev, [key]: value }));
    // Clear validation error on change
    if (fieldErrors[key]) {
      setFieldErrors(prev => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const handleWebhookChange = (key, value) => {
    setWebhookConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleConfigChange = (key, value) => {
    setGatewayConfigValues(prev => ({ ...prev, [key]: value }));
  };

  const toggleFieldVisibility = (key) => {
    setVisibleFields(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (saving) return;

    if (!gatewayName.trim()) {
      setError('Gateway name is required');
      return;
    }

    // Validate credentials (only new values on create, or new values on edit)
    if (!isEditing) {
      const validation = validateGatewayCredentials(gatewayType, credentials);
      if (!validation.isValid) {
        setFieldErrors(validation.errors);
        setError('Please fix credential validation errors before saving');
        return;
      }
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      setFieldErrors({});

      // Build credentials payload — only include non-empty new values
      const credPayload = {};
      let hasNewCredentials = false;

      if (gwConfig) {
        gwConfig.credentialFields.forEach(field => {
          if (credentials[field.key]?.trim()) {
            credPayload[field.key] = credentials[field.key].trim();
            hasNewCredentials = true;
          }
        });
      }

      // Always include environment in credentials (ElavonGateway reads it from credentials.environment)
      credPayload.environment = environment;

      // Build config payload — environment + webhook fields + behavior config
      const configPayload = { environment };

      if (gwConfig) {
        gwConfig.webhookFields.forEach(field => {
          if (webhookConfig[field.key]?.trim()) {
            configPayload[field.key] = webhookConfig[field.key].trim();
          }
        });

        gwConfig.configFields.forEach(field => {
          if (gatewayConfigValues[field.key] !== undefined) {
            configPayload[field.key] = gatewayConfigValues[field.key];
          }
        });
      }

      const payload = {
        gatewayName: gatewayName.trim(),
        gatewayType,
        isEnabled,
        isDefault,
        notes: notes.trim() || null,
        config: configPayload
      };

      if (hasNewCredentials || !isEditing) {
        payload.credentials = credPayload;
      }

      if (isEditing) {
        await updatePlatformGateway(gatewayId, payload);
        setSuccess('Gateway configuration updated');
      } else {
        await createPlatformGateway(payload);
        setSuccess('Gateway created successfully');
      }

      setCredentials({});
      setWebhookConfig({});
      setTimeout(() => {
        setSuccess('');
        if (!isEditing) {
          onSaved();
        } else {
          loadGateway();
        }
      }, 1500);
    } catch (err) {
      console.error('Error saving gateway:', err);
      setError(err.response?.data?.error?.message || 'Failed to save gateway configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          <p className="text-sm text-gray-500">Loading gateway configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Gateways
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <CreditCard className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEditing ? 'Edit Gateway' : 'Add Gateway'}
            </h1>
            <p className="text-sm text-gray-500">
              {isEditing ? 'Update platform gateway credentials and configuration' : 'Configure a new payment gateway with master credentials'}
            </p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Gateway Details */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Gateway Details</h2>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label htmlFor="gw-name" className="block text-sm font-medium text-gray-900 mb-1">
              Gateway Name
            </label>
            <input
              id="gw-name"
              type="text"
              value={gatewayName}
              onChange={(e) => setGatewayName(e.target.value)}
              placeholder="Elavon EPG"
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              disabled={saving}
            />
          </div>

          <div>
            <label htmlFor="gw-type" className="block text-sm font-medium text-gray-900 mb-1">
              Gateway Type
            </label>
            <select
              id="gw-type"
              value={gatewayType}
              onChange={(e) => setGatewayType(e.target.value)}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              disabled={saving || isEditing}
            >
              {getGatewayTypes().map(t => (
                <option key={t.value} value={t.value} disabled={!t.available}>
                  {t.label}{!t.available ? ' (Coming Soon)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Environment — from gateway registry */}
          {gwConfig?.environments && (
            <div>
              <label htmlFor="gw-env" className="block text-sm font-medium text-gray-900 mb-1">
                Environment
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Determines API base URL and HPF script source
              </p>
              <select
                id="gw-env"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                disabled={saving}
              >
                {gwConfig.environments.map(env => (
                  <option key={env.value} value={env.value}>{env.label}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="gw-enabled" className="flex items-center gap-3 cursor-pointer">
              <input
                id="gw-enabled"
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
                className="rounded border-gray-300 text-slate-800 focus:ring-slate-500 w-5 h-5"
                disabled={saving}
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Enable Gateway</span>
                <p className="text-xs text-gray-500">When disabled, this gateway cannot be assigned to tenants</p>
              </div>
            </label>
          </div>

          <div>
            <label htmlFor="gw-default" className="flex items-center gap-3 cursor-pointer">
              <input
                id="gw-default"
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded border-gray-300 text-slate-800 focus:ring-slate-500 w-5 h-5"
                disabled={saving}
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Default Gateway</span>
                <p className="text-xs text-gray-500">Mark as the default gateway for direct merchants (non-SO)</p>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* API Credentials — dynamically rendered from registry */}
      {gwConfig?.credentialFields?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">{gwConfig.label} API Credentials</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Master credentials for {gwConfig.label} API authentication
            </p>
          </div>
          <div className="p-6 space-y-5">
            {gwConfig.credentialFields.map(field => (
              <CredentialField
                key={field.key}
                field={field}
                value={credentials[field.key]}
                maskedValue={maskedCredentials[field.key]}
                isEditing={isEditing}
                saving={saving}
                visibleFields={visibleFields}
                onToggleVisibility={toggleFieldVisibility}
                onChange={handleCredentialChange}
                validationError={fieldErrors[field.key]}
              />
            ))}
          </div>
        </div>
      )}

      {/* Webhook Configuration — dynamically rendered from registry */}
      {gwConfig?.webhookFields?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">{gwConfig.label} Webhook Configuration</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Webhook authentication for receiving push notifications at{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                {gwConfig.webhookUrl}
              </code>
            </p>
          </div>
          <div className="p-6 space-y-5">
            {gwConfig.webhookFields.map(field => (
              <CredentialField
                key={field.key}
                field={field}
                value={webhookConfig[field.key]}
                maskedValue={maskedWebhook[field.key]}
                isEditing={isEditing}
                saving={saving}
                visibleFields={visibleFields}
                onToggleVisibility={toggleFieldVisibility}
                onChange={handleWebhookChange}
                validationError={null}
              />
            ))}
          </div>
        </div>
      )}

      {/* Gateway Behavior Config — dynamically rendered from registry */}
      {gwConfig?.configFields?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">{gwConfig.label} Settings</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Default behavior configuration for payment processing
            </p>
          </div>
          <div className="p-6 space-y-5">
            {gwConfig.configFields.map(field => (
              <ConfigField
                key={field.key}
                field={field}
                value={gatewayConfigValues[field.key]}
                saving={saving}
                onChange={handleConfigChange}
              />
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Notes</h2>
        </div>
        <div className="p-6">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes about this gateway configuration..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
            disabled={saving}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-4 mb-8">
        <button
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !gatewayName.trim()}
          className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            isEditing ? 'Save Changes' : 'Create Gateway'
          )}
        </button>
      </div>
    </div>
  );
}

/**
 * GatewayConfig — Root component that switches between list and form views
 */
function GatewayConfig() {
  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadGateways = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getPlatformGateways();
      setGateways(result.data || []);
    } catch (err) {
      console.error('Error loading gateways:', err);
      setError('Failed to load gateways');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGateways();
  }, [loadGateways]);

  const handleSelect = (id) => {
    setSelectedId(id);
    setView('edit');
  };

  const handleCreate = () => {
    setSelectedId(null);
    setView('create');
  };

  const handleBack = () => {
    setSelectedId(null);
    setView('list');
    loadGateways();
  };

  if (view === 'create' || view === 'edit') {
    return (
      <GatewayForm
        gatewayId={selectedId}
        onBack={handleBack}
        onSaved={handleBack}
      />
    );
  }

  return (
    <>
      {error && (
        <div className="max-w-3xl mx-auto mb-6">
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
      <GatewayList
        gateways={gateways}
        loading={loading}
        onSelect={handleSelect}
        onCreate={handleCreate}
      />
    </>
  );
}

export default GatewayConfig;
